import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Plug, Plus, Trash2, Save, Zap, Loader2, Database, CheckCircle2, XCircle, LayoutGrid, Globe, Star, Check, Ban } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { api } from '@/lib/api';

const STARTER_TABLES = JSON.stringify([
  { name: 'items', path: '/items', method: 'GET', data_path: 'data', params: {}, fields: [] },
], null, 2);

const EMPTY = {
  name: '', description: '', enabled: true,
  base_url: '', test_path: '', headers: '{}',
  auth_type: 'none', auth_header: 'X-API-Key', auth_key: '', auth_query_param: 'api_key',
  auth_username: '', auth_password: '',
  tables: STARTER_TABLES,
};

function buildManifest(form) {
  const auth = { type: form.auth_type };
  if (form.auth_type === 'api_key') { auth.header = form.auth_header; auth.key = form.auth_key; }
  else if (form.auth_type === 'bearer') { auth.key = form.auth_key; }
  else if (form.auth_type === 'query_param') { auth.query_param = form.auth_query_param; auth.key = form.auth_key; }
  else if (form.auth_type === 'basic') { auth.username = form.auth_username; auth.password = form.auth_password; }
  return {
    base_url: form.base_url.trim(),
    test_path: form.test_path.trim() || undefined,
    headers: JSON.parse(form.headers || '{}'),
    auth,
    tables: JSON.parse(form.tables || '[]'),
  };
}

function manifestToForm(c) {
  const m = c.manifest || {};
  const a = m.auth || {};
  return {
    name: c.name, description: c.description || '', enabled: c.enabled,
    base_url: m.base_url || '', test_path: m.test_path || '',
    headers: JSON.stringify(m.headers || {}, null, 0),
    auth_type: a.type || 'none', auth_header: a.header || 'X-API-Key', auth_key: a.key || '',
    auth_query_param: a.query_param || 'api_key', auth_username: a.username || '', auth_password: a.password || '',
    tables: JSON.stringify(m.tables || [], null, 2),
  };
}

export default function CustomConnectorsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [communityTemplates, setCommunityTemplates] = useState([]);
  const [publishing, setPublishing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.listCustomConnectors(); setItems(res.items || []); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setForm(EMPTY); setEditing('new'); setTestResult(null); };
  const startEdit = (c) => { setForm(manifestToForm(c)); setEditing(c.connector_id); setTestResult(null); };

  const openGallery = async () => {
    setGalleryOpen(true);
    if (templates.length === 0) {
      try { const res = await api.getConnectorGallery(); setTemplates(res.templates || []); }
      catch (e) { toast.error(e.message); }
    }
    loadCommunity();
  };

  const loadCommunity = async () => {
    try { const res = await api.getCommunityGallery('connector'); setCommunityTemplates(res.templates || []); }
    catch (e) { /* optional */ }
  };

  const publishConnector = async (c) => {
    setPublishing(c.type_key);
    try {
      const res = await api.publishToCommunity('connector', c.connector_id);
      toast.success(res.status === 'pending'
        ? `"${c.name}" submitted — awaiting super-admin approval`
        : `"${c.name}" published to the Community Gallery`);
      loadCommunity();
    } catch (e) { toast.error(e.message); }
    finally { setPublishing(null); }
  };

  const unpublishCommunity = async (t) => {
    if (!window.confirm(`Remove "${t.name}" from the Community Gallery?`)) return;
    try { await api.unpublishCommunity(t.gallery_id); toast.success('Unpublished'); loadCommunity(); }
    catch (e) { toast.error(e.message); }
  };

  const moderate = async (t, patch) => {
    try { await api.moderateCommunity(t.gallery_id, patch); loadCommunity(); }
    catch (e) { toast.error(e.message); }
  };

  const applyTemplate = (t) => {
    if (t.gallery_id) api.trackCommunityImport(t.gallery_id).catch(() => {});
    setForm(manifestToForm({ name: t.name, description: t.description, enabled: true, manifest: t.manifest }));
    setEditing('new'); setTestResult(null); setGalleryOpen(false);
    if (t.auth_help) toast.info(t.auth_help, { duration: 8000 });
  };

  const safeManifest = () => {
    try { return buildManifest(form); }
    catch (e) { toast.error('Invalid JSON in headers or tables: ' + e.message); return null; }
  };

  const test = async () => {
    const manifest = safeManifest();
    if (!manifest) return;
    if (!manifest.base_url) return toast.error('base_url is required');
    setTesting(true); setTestResult(null);
    try { const r = await api.testCustomConnector(manifest); setTestResult(r); }
    catch (e) { setTestResult({ success: false, message: e.message }); }
    finally { setTesting(false); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Give your connector a name');
    const manifest = safeManifest();
    if (!manifest) return;
    setSaving(true);
    try {
      const payload = { name: form.name, description: form.description, enabled: form.enabled, manifest };
      if (editing === 'new') { await api.createCustomConnector(payload); toast.success('Connector created — now available in Data Sources'); }
      else { await api.updateCustomConnector(editing, payload); toast.success('Saved'); }
      await load(); setEditing(null);
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try { await api.deleteCustomConnector(c.connector_id); toast.success('Deleted'); await load(); }
    catch (e) { toast.error(e.message); }
  };

  if (editing) {
    return (
      <div className="p-6 max-w-3xl mx-auto" data-testid="custom-connector-editor-page">
        <div className="flex items-center justify-between mb-5">
          <div>
            <button onClick={() => setEditing(null)} className="text-sm text-primary hover:underline mb-1" data-testid="cc-back-btn">← Back to connectors</button>
            <h1 className="text-2xl font-bold">{editing === 'new' ? 'New Custom Connector' : 'Edit Connector'}</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={test} disabled={testing} data-testid="cc-test-btn">{testing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}Test</Button>
            <Button onClick={save} disabled={saving} data-testid="cc-save-btn">{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save</Button>
          </div>
        </div>

        {testResult && (
          <Card className={`p-3 mb-4 flex items-center gap-2 ${testResult.success ? 'border-emerald-500/40' : 'border-red-500/40'}`} data-testid="cc-test-result">
            {testResult.success ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
            <span className="text-sm">{testResult.message}</span>
          </Card>
        )}

        <div className="space-y-4">
          <Card className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Acme REST API" data-testid="cc-name-input" /></div>
              <div className="flex items-center gap-2 pt-6"><Switch checked={form.enabled} onCheckedChange={v => setForm({ ...form, enabled: v })} data-testid="cc-enabled-switch" /><Label className="!mb-0">Enabled</Label></div>
            </div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Base URL</Label><Input value={form.base_url} onChange={e => setForm({ ...form, base_url: e.target.value })} placeholder="https://api.example.com" data-testid="cc-baseurl-input" /></div>
            <div><Label>Test path (optional)</Label><Input value={form.test_path} onChange={e => setForm({ ...form, test_path: e.target.value })} placeholder="/health" data-testid="cc-testpath-input" /></div>
          </Card>

          <Card className="p-4 space-y-3">
            <div className="text-sm font-semibold">Authentication</div>
            <select className="h-9 w-full rounded-md border bg-background text-sm px-2" value={form.auth_type} onChange={e => setForm({ ...form, auth_type: e.target.value })} data-testid="cc-authtype-select">
              <option value="none">None</option><option value="api_key">API key (header)</option><option value="bearer">Bearer token</option><option value="query_param">API key (query param)</option><option value="basic">Basic auth</option>
            </select>
            {form.auth_type === 'api_key' && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Header name</Label><Input value={form.auth_header} onChange={e => setForm({ ...form, auth_header: e.target.value })} /></div>
                <div><Label>Key</Label><Input type="password" value={form.auth_key} onChange={e => setForm({ ...form, auth_key: e.target.value })} /></div>
              </div>
            )}
            {form.auth_type === 'bearer' && (<div><Label>Token</Label><Input type="password" value={form.auth_key} onChange={e => setForm({ ...form, auth_key: e.target.value })} /></div>)}
            {form.auth_type === 'query_param' && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Param name</Label><Input value={form.auth_query_param} onChange={e => setForm({ ...form, auth_query_param: e.target.value })} /></div>
                <div><Label>Key</Label><Input type="password" value={form.auth_key} onChange={e => setForm({ ...form, auth_key: e.target.value })} /></div>
              </div>
            )}
            {form.auth_type === 'basic' && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Username</Label><Input value={form.auth_username} onChange={e => setForm({ ...form, auth_username: e.target.value })} /></div>
                <div><Label>Password</Label><Input type="password" value={form.auth_password} onChange={e => setForm({ ...form, auth_password: e.target.value })} /></div>
              </div>
            )}
            <div><Label>Extra headers (JSON)</Label><Input value={form.headers} onChange={e => setForm({ ...form, headers: e.target.value })} placeholder='{"Accept":"application/json"}' className="font-mono text-xs" /></div>
          </Card>

          <Card className="p-4">
            <div className="text-sm font-semibold mb-1">Tables (JSON)</div>
            <p className="text-[11px] text-muted-foreground mb-2">Each table maps to a REST endpoint. <code>data_path</code> is a dot-path to the row array (e.g. <code>data.items</code>).</p>
            <Textarea value={form.tables} onChange={e => setForm({ ...form, tables: e.target.value })} className="font-mono text-xs h-56" spellCheck={false} data-testid="cc-tables-editor" />
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="custom-connectors-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Plug className="w-6 h-6 text-primary" /> Connector SDK</h1>
          <p className="text-sm text-muted-foreground mt-1">Define declarative REST data connectors. They appear in Data Sources once enabled.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openGallery} data-testid="cc-gallery-btn"><LayoutGrid className="w-4 h-4 mr-1" />Browse Gallery</Button>
          <Button onClick={startNew} data-testid="cc-new-btn"><Plus className="w-4 h-4 mr-1" />New Connector</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="cc-empty">
          <Plug className="w-10 h-10 mx-auto mb-3 opacity-40" />
          No custom connectors yet. Build one to plug any REST API into INZARA.
          <div className="mt-4"><Button variant="outline" onClick={openGallery} data-testid="cc-empty-gallery-btn"><LayoutGrid className="w-4 h-4 mr-1" />Browse the Gallery</Button></div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(c => (
            <Card key={c.connector_id} className="p-4 hover:shadow-md transition-shadow" data-testid={`cc-card-${c.type_key}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2"><Database className="w-5 h-5 text-primary" /><span className="font-semibold">{c.name}</span></div>
                <Badge variant={c.enabled ? 'default' : 'secondary'}>{c.enabled ? 'Enabled' : 'Disabled'}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-2 line-clamp-2 min-h-[2rem]">{c.description || c.manifest?.base_url}</p>
              <div className="text-[10px] text-muted-foreground mt-1 font-mono">{c.type_key} · {(c.manifest?.tables || []).length} table(s)</div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => startEdit(c)} data-testid={`cc-edit-${c.type_key}`}>Edit</Button>
                <Button size="sm" variant="ghost" title="Publish to Community Gallery" disabled={publishing === c.type_key} onClick={() => publishConnector(c)} data-testid={`cc-publish-${c.type_key}`}>
                  {publishing === c.type_key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
                </Button>
                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => remove(c)} data-testid={`cc-delete-${c.type_key}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="cc-gallery-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><LayoutGrid className="w-5 h-5 text-primary" /> Connector Gallery</DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-sm text-muted-foreground -mt-1">Pick a template to pre-fill the connector — then just add your API key.</DialogDescription>
          {templates.length === 0 ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              {templates.map(t => {
                const Icon = LucideIcons[t.icon] || Database;
                return (
                  <button key={t.key} onClick={() => applyTemplate(t)} data-testid={`cc-template-${t.key}`}
                    className="text-left border rounded-lg p-3 hover:border-primary hover:shadow-md transition-all bg-card">
                    <div className="flex items-center gap-2">
                      <Icon className="w-5 h-5 text-primary shrink-0" />
                      <span className="font-semibold">{t.name}</span>
                      <Badge variant="secondary" className="ml-auto text-[10px]">{t.category}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{t.description}</p>
                    <div className="text-[10px] text-muted-foreground mt-1">{(t.manifest?.tables || []).length} table(s) · {t.manifest?.auth?.type} auth</div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Community-published connectors (shared across tenants) */}
          <div className="mt-6 pt-4 border-t border-border/50">
            <div className="flex items-center gap-2 mb-1">
              <Globe className="w-4 h-4 text-primary" />
              <h3 className="font-semibold text-sm">Community Gallery</h3>
              <Badge variant="secondary" className="text-[10px]">{communityTemplates.length}</Badge>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Connectors published by admins across all tenants. Publish your own with the <Globe className="w-3 h-3 inline" /> button on a saved connector.</p>
            {communityTemplates.length === 0 ? (
              <p className="text-xs text-muted-foreground italic py-2" data-testid="cc-community-empty">Nothing published yet — be the first to share a connector!</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {communityTemplates.map(t => {
                  const Icon = LucideIcons[t.icon] || Database;
                  return (
                    <div key={t.gallery_id} data-testid={`cc-community-${t.gallery_id}`}
                      className={`text-left border rounded-lg p-3 hover:border-primary hover:shadow-md transition-all bg-card flex flex-col ${t.featured ? 'border-amber-400/60 ring-1 ring-amber-400/30' : ''}`}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-5 h-5 text-primary shrink-0" />
                        <span className="font-semibold">{t.name}</span>
                        {t.featured && <Badge className="text-[10px] gap-1 bg-amber-500/15 text-amber-500 border-amber-500/30" data-testid={`cc-community-featured-${t.gallery_id}`}><Star className="w-2.5 h-2.5 fill-amber-500" />Featured</Badge>}
                        {t.status && t.status !== 'approved' && <Badge variant="outline" className={`text-[10px] ${t.status === 'rejected' ? 'text-red-500 border-red-500/30' : 'text-amber-500 border-amber-500/30'}`}>{t.status}</Badge>}
                        <Badge variant="outline" className="ml-auto text-[10px] gap-1"><Globe className="w-2.5 h-2.5" />Community</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{t.description || t.manifest?.base_url}</p>
                      <div className="text-[10px] text-muted-foreground/70 mt-1">by {t.published_by_email || 'unknown'} · {(t.manifest?.tables || []).length} table(s) · {t.downloads || 0} imports</div>
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="outline" className="flex-1" onClick={() => applyTemplate(t)} data-testid={`cc-community-use-${t.gallery_id}`}>Use template</Button>
                        {t.is_owner && (
                          <Button size="sm" variant="ghost" className="text-red-500" title="Unpublish" onClick={() => unpublishCommunity(t)} data-testid={`cc-community-unpublish-${t.gallery_id}`}><Trash2 className="w-4 h-4" /></Button>
                        )}
                      </div>
                      {t.can_moderate && (
                        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-border/40">
                          <span className="text-[10px] text-muted-foreground">Curate:</span>
                          <Button size="sm" variant="ghost" className={`h-7 text-[11px] ${t.featured ? 'text-amber-500' : 'text-muted-foreground'}`} onClick={() => moderate(t, { featured: !t.featured })} data-testid={`cc-community-feature-${t.gallery_id}`}>
                            <Star className={`w-3.5 h-3.5 mr-1 ${t.featured ? 'fill-amber-500' : ''}`} />{t.featured ? 'Unfeature' : 'Feature'}
                          </Button>
                          {t.status !== 'approved'
                            ? <Button size="sm" variant="ghost" className="h-7 text-[11px] text-emerald-500" onClick={() => moderate(t, { status: 'approved' })} data-testid={`cc-community-approve-${t.gallery_id}`}><Check className="w-3.5 h-3.5 mr-1" />Approve</Button>
                            : <Button size="sm" variant="ghost" className="h-7 text-[11px] text-red-500" onClick={() => moderate(t, { status: 'rejected' })} data-testid={`cc-community-reject-${t.gallery_id}`}><Ban className="w-3.5 h-3.5 mr-1" />Reject</Button>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
